<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <app-admin-machine-learning-nav></app-admin-machine-learning-nav>

    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
      <div>
        <div class="d-flex align-items-center gap-2 mb-1">
          <h4 class="fw-bold mb-0">Machine Learning Operations</h4>
          <button type="button" class="ml-page-help-link" (click)="pageGuide.scrollToGuide()" title="How to use this page" aria-label="Go to the How to use this page guide">?</button>
          <span *ngIf="overview" class="badge" [ngClass]="overallStatusClass">
            {{ overview.overallStatus }}
          </span>
        </div>
        <div class="text-muted small">
          Lifecycle readiness, risk queues, operational alerts, liquidity and deployment health in one view.
        </div>
      </div>

      <button class="btn btn-primary btn-sm align-self-start align-self-lg-auto" type="button" (click)="load()" [disabled]="loading">
        <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
        Refresh all
      </button>
    </div>
    <div *ngIf="error" class="alert alert-danger d-flex justify-content-between align-items-center gap-3">
      <span>{{ error }}</span>
      <button class="btn btn-outline-danger btn-sm" type="button" (click)="load()">Retry</button>
    </div>

    <div *ngIf="loading && !snapshot" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <div class="text-muted small mt-3">Loading the ML operations overview...</div>
    </div>

    <ng-container *ngIf="snapshot as operations">
      <div *ngIf="partialErrors.length" class="alert alert-warning small">
        <div class="fw-semibold mb-1">Some ML modules could not be loaded.</div>
        <div *ngFor="let item of partialErrors">{{ item }}</div>
      </div>

      <section class="row g-3 mb-4">
        <div class="col-sm-6 col-xl-3">
          <a class="ml-stat-card ml-stat-link h-100 p-3 d-block text-decoration-none" routerLink="/admin/machine-learning/withdrawal-risk">
            <div class="d-flex justify-content-between align-items-start gap-2">
              <div class="stat-label">Withdrawal reviews</div>
              <span class="badge" [ngClass]="openWithdrawalReviews ? 'text-bg-warning text-dark' : 'text-bg-success'">{{ openWithdrawalReviews }}</span>
            </div>
            <div class="stat-value">{{ openWithdrawalReviews | number }}</div>
            <div class="stat-note">Pending, submitting or awaiting reconciliation</div>
          </a>
        </div>

        <div class="col-sm-6 col-xl-3">
          <a class="ml-stat-card ml-stat-link h-100 p-3 d-block text-decoration-none" routerLink="/admin/machine-learning/deposit-risk">
            <div class="d-flex justify-content-between align-items-start gap-2">
              <div class="stat-label">Deposit reviews</div>
              <span class="badge" [ngClass]="openDepositReviews ? 'text-bg-warning text-dark' : 'text-bg-success'">{{ openDepositReviews }}</span>
            </div>
            <div class="stat-value">{{ openDepositReviews | number }}</div>
            <div class="stat-note">Open compliance, source-of-funds and escalation work</div>
          </a>
        </div>

        <div class="col-sm-6 col-xl-3">
          <a class="ml-stat-card ml-stat-link h-100 p-3 d-block text-decoration-none" routerLink="/admin/machine-learning/liquidity">
            <div class="d-flex justify-content-between align-items-start gap-2">
              <div class="stat-label">Critical corridors</div>
              <span class="badge" [ngClass]="criticalLiquidityCorridors ? 'text-bg-danger' : 'text-bg-success'">{{ criticalLiquidityCorridors }}</span>
            </div>
            <div class="stat-value">{{ criticalLiquidityCorridors | number }}</div>
            <div class="stat-note">Liquidity corridors requiring urgent attention</div>
          </a>
        </div>

        <div class="col-sm-6 col-xl-3">
          <a class="ml-stat-card ml-stat-link h-100 p-3 d-block text-decoration-none" routerLink="/admin/machine-learning/deployment-health">
            <div class="d-flex justify-content-between align-items-start gap-2">
              <div class="stat-label">Deployment health</div>
              <span class="badge" [ngClass]="deploymentStatusClass">{{ operations.deploymentHealth?.healthStatus || 'No deployment' }}</span>
            </div>
            <div class="stat-value version-value">{{ operations.deploymentHealth?.modelVersion || overview?.currentDeploymentVersion || 'Deterministic only' }}</div>
            <div class="stat-note">{{ operations.deploymentHealth?.canaryPercentage != null ? (operations.deploymentHealth?.canaryPercentage + '% canary') : 'No ML.NET canary active' }}</div>
          </a>
        </div>
      </section>

      <div class="row g-4 mb-4">
        <div class="col-xl-8">
          <section class="ml-panel p-3 p-lg-4 h-100">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-1">Operational alerts</h5>
                <div class="text-muted small">Combined risk, liquidity, deployment and reliability signals.</div>
              </div>
              <span class="badge" [ngClass]="activeAlertCount ? 'text-bg-danger' : 'text-bg-success'">{{ activeAlertCount }}</span>
            </div>

            <div *ngIf="!combinedAlerts.length" class="empty-state text-center py-4">
              <div class="fs-3 mb-2">✓</div>
              <div class="fw-semibold">No active ML operational alerts</div>
              <div class="text-muted small mt-1">All loaded monitoring modules are within their configured thresholds.</div>
            </div>

            <a *ngFor="let alert of combinedAlerts | slice:0:8; trackBy: trackAlert"
               [routerLink]="alert.route"
               class="alert-row text-decoration-none"
               [ngClass]="getSeverityClass(alert.severity)">
              <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-2">
                <div>
                  <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <span class="badge" [ngClass]="getSeverityBadgeClass(alert.severity)">{{ alert.severity }}</span>
                    <span class="fw-semibold text-dark">{{ alert.source }}</span>
                    <span *ngIf="alert.count > 1" class="badge text-bg-light border">{{ alert.count }}</span>
                  </div>
                  <div class="small text-muted">{{ alert.message }}</div>
                </div>
                <span class="small fw-semibold text-primary text-nowrap">Open module →</span>
              </div>
            </a>

            <div *ngIf="combinedAlerts.length > 8" class="text-muted small mt-3">
              Showing the eight highest-priority alerts of {{ combinedAlerts.length }}.
            </div>
          </section>
        </div>

        <div class="col-xl-4">
          <section class="ml-panel p-3 p-lg-4 h-100">
            <h5 class="fw-bold mb-1">Quick actions</h5>
            <div class="text-muted small mb-3">Open the module that needs attention.</div>

            <a *ngFor="let link of quickLinks; trackBy: trackQuickLink"
               [routerLink]="link.route"
               class="quick-link text-decoration-none">
              <div class="flex-grow-1">
                <div class="fw-semibold text-dark">{{ link.title }}</div>
                <div class="small text-muted">{{ link.description }}</div>
              </div>
              <span *ngIf="link.badge != null" class="badge" [ngClass]="link.badge ? 'text-bg-warning text-dark' : 'text-bg-light text-dark border'">{{ link.badge }}</span>
              <span *ngIf="link.status" class="badge" [ngClass]="getStatusClass(link.status)">{{ link.status }}</span>
            </a>
          </section>
        </div>
      </div>

      <ng-container *ngIf="overview as data">
        <section class="ml-hero-card p-4 mb-4">
          <div class="row g-4 align-items-center">
            <div class="col-lg-8">
              <div class="text-uppercase text-muted small fw-semibold mb-2">Recommended lifecycle action</div>
              <h5 class="fw-bold mb-2">{{ data.recommendedNextAction }}</h5>
              <div class="text-muted small">
                Scenario: {{ data.scenario }} · Retrieved {{ data.retrievedAt | date:'medium' }}
              </div>
            </div>

            <div class="col-lg-4">
              <div class="d-flex justify-content-lg-end">
                <div class="status-panel text-lg-end">
                  <div class="text-muted small">Overall lifecycle status</div>
                  <div class="fs-4 fw-bold mt-1">{{ data.overallStatus }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="row g-3 mb-4">
          <div class="col-sm-6 col-xl-3">
            <div class="ml-stat-card h-100 p-3">
              <div class="stat-label">Current deployment</div>
              <div class="stat-value version-value">{{ data.currentDeploymentVersion || 'Deterministic only' }}</div>
              <div class="stat-note" *ngIf="data.currentCanaryPercentage != null">{{ data.currentCanaryPercentage }}% canary rollout</div>
              <div class="stat-note" *ngIf="data.currentCanaryPercentage == null">No ML.NET canary active</div>
            </div>
          </div>

          <div class="col-sm-6 col-xl-3">
            <div class="ml-stat-card h-100 p-3">
              <div class="stat-label">Shadow model</div>
              <div class="stat-value version-value">{{ data.currentShadowVersion || 'None selected' }}</div>
              <div class="stat-note">Database-governed comparison candidate</div>
            </div>
          </div>

          <div class="col-sm-6 col-xl-3">
            <div class="ml-stat-card h-100 p-3">
              <div class="stat-label">Latest candidate</div>
              <div class="stat-value version-value">{{ data.latestCandidateVersion || 'No candidate' }}</div>
              <div class="stat-note">Most recent registered training candidate</div>
            </div>
          </div>

          <div class="col-sm-6 col-xl-3">
            <div class="ml-stat-card h-100 p-3">
              <div class="d-flex justify-content-between align-items-center gap-2">
                <div class="stat-label">Labelled predictions</div>
                <span class="badge text-bg-light border">{{ labelledCoveragePercent }}%</span>
              </div>
              <div class="stat-value">{{ data.labelledPredictions | number }}</div>
              <div class="progress mt-2" role="progressbar" aria-label="Labelled prediction coverage" [attr.aria-valuenow]="labelledCoveragePercent" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar" [style.width.%]="labelledCoveragePercent"></div>
              </div>
              <div class="stat-note mt-2">of {{ data.totalPredictions | number }} total predictions</div>
            </div>
          </div>
        </section>

        <div class="row g-4">
          <div class="col-xl-8">
            <section class="ml-panel p-3 p-lg-4 h-100">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 class="fw-bold mb-1">Lifecycle stages</h5>
                  <div class="text-muted small">Ordered readiness across the login-risk model lifecycle.</div>
                </div>
                <span class="badge text-bg-light border">{{ data.stages.length }} stages</span>
              </div>

              <div class="lifecycle-stage" *ngFor="let stage of data.stages; trackBy: trackStage">
                <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-2">
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                      <div class="fw-bold">{{ stage.name }}</div>
                      <span class="badge" [ngClass]="getStatusClass(stage.status)">{{ stage.status }}</span>
                      <span *ngIf="stage.currentVersion" class="version-chip">{{ stage.currentVersion }}</span>
                    </div>
                    <div class="text-muted small">{{ stage.summary }}</div>
                  </div>

                  <div *ngIf="stage.progressPercent != null" class="stage-progress-value">
                    {{ normalizedProgress(stage.progressPercent) | number:'1.0-1' }}%
                  </div>
                </div>

                <div *ngIf="stage.progressPercent != null" class="progress mt-3" role="progressbar" [attr.aria-valuenow]="normalizedProgress(stage.progressPercent)" aria-valuemin="0" aria-valuemax="100">
                  <div class="progress-bar" [ngClass]="getProgressBarClass(stage)" [style.width.%]="normalizedProgress(stage.progressPercent)"></div>
                </div>

                <div *ngIf="stage.issues?.length" class="stage-issues mt-3">
                  <div class="small fw-semibold text-danger mb-1">Blocking issues</div>
                  <ul class="small mb-0 ps-3">
                    <li *ngFor="let issue of stage.issues">{{ issue }}</li>
                  </ul>
                </div>

                <div *ngIf="stage.recommendedAction" class="stage-action mt-3">
                  <span class="small fw-semibold">Recommended:</span>
                  <span class="small text-muted ms-1">{{ stage.recommendedAction }}</span>
                </div>

                <code *ngIf="stage.actionEndpoint" class="action-endpoint d-block mt-2">{{ stage.actionEndpoint }}</code>
              </div>

              <div *ngIf="!data.stages.length" class="text-muted small text-center py-4">No lifecycle stages were returned.</div>
            </section>
          </div>

          <div class="col-xl-4">
            <section class="ml-panel p-3 p-lg-4 mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 class="fw-bold mb-1">Priority actions</h5>
                  <div class="text-muted small">Items requiring administrative attention.</div>
                </div>
                <span class="badge text-bg-danger" *ngIf="data.actionItems.length">{{ data.actionItems.length }}</span>
              </div>

              <div *ngIf="!data.actionItems.length" class="empty-state text-center py-4">
                <div class="fs-3 mb-2">✓</div>
                <div class="fw-semibold">No outstanding lifecycle actions</div>
                <div class="text-muted small mt-1">The current ML lifecycle has no reported action items.</div>
              </div>

              <div class="action-card" [ngClass]="getSeverityClass(action.severity)" *ngFor="let action of data.actionItems; trackBy: trackAction">
                <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <div class="fw-bold">{{ action.title }}</div>
                  <span class="badge" [ngClass]="getSeverityBadgeClass(action.severity)">{{ action.severity }}</span>
                </div>
                <div class="small text-muted mb-2">{{ action.message }}</div>
                <div class="small fw-semibold">{{ action.recommendedAction }}</div>
                <a *ngIf="getActionRoute(action) as route" [routerLink]="route" class="btn btn-outline-primary btn-sm mt-2">Open relevant module</a>
                <code *ngIf="action.actionEndpoint" class="action-endpoint d-block mt-2">{{ action.actionEndpoint }}</code>
              </div>
            </section>

            <section class="ml-panel p-3 p-lg-4">
              <h6 class="fw-bold mb-3">Control-plane safety</h6>
              <div class="safety-item"><span class="safety-dot bg-success"></span><span>Overview access is read-only.</span></div>
              <div class="safety-item"><span class="safety-dot bg-success"></span><span>Promotion and rollback remain separate SuperAdmin actions.</span></div>
              <div class="safety-item"><span class="safety-dot bg-success"></span><span>Financial workflows remain controlled by their existing services.</span></div>
              <div class="safety-item"><span class="safety-dot bg-success"></span><span>Partial module failures do not hide healthy operational data.</span></div>
            </section>
          </div>
        </div>
      </ng-container>
    </ng-container>

        <app-admin-ml-page-guide #pageGuide id="ml-page-guide" tabindex="-1"
      [purpose]="'Use this as the daily starting point for machine-learning operations and risk queues.'"
      [steps]="['Review Critical and High alerts, then open any withdrawal, deposit, liquidity, or deployment queue that needs attention.', 'Follow the highest-priority lifecycle action item and use the quick links to complete the work in the correct module.', 'Refresh the overview after completing an action and confirm that queue badges, alerts, and readiness blockers have changed as expected.']"
      [important]="'This page summarizes activity only. Complete approvals, reviews, training, deployment, and rollback actions on their dedicated pages.'"
      [accessNote]="'Admin and SuperAdmin can view the overview; write actions remain protected on the relevant pages.'">
    </app-admin-ml-page-guide>

    <app-footer></app-footer>
  </main>
</div>
